import React, {ReactNode, memo, useCallback, useEffect, useRef, useState} from "react"
import "./index.scss"
import {setBellStatusAction, setEntityListAction, setIntercomStatusAction} from "../../store/control/actions"
import iconCallGreen from "../../assets/images/icons/call-screen/icon-call-green.png"
import iconCallEndRed from "../../assets/images/icons/call-screen/call-end-red.png"
import iconBlockRed from "../../assets/images/icons/block.png"
import {useRPC} from "../../hooks/rpc"
import yellowDoorIcon from "../../assets/images/icons/intercom/door_open1_icon.png"
import blueDoorIcon from "../../assets/images/icons/intercom/door_open2_icon.png"
import pingDoorIcon from "../../assets/images/icons/intercom/door_open3_icon.png"
import limeDoorIcon from "../../assets/images/icons/intercom/door_open4_icon.png"
import {Timer} from "../../components/Timer"
import {useDispatch, useSelector} from "react-redux"
import {IntercomModel} from "../../types/intercom"
import {onClickHandler} from "../../utils/onclick"
import {useOrientation} from "../../hooks/orientation"
import {useCurrentGateway} from "../../hooks/current-gateway"
import {CameraRenderer} from "../../components/CameraRenderer"
import {WindowUtil} from "../../utils/window"
import {useBackground} from "../../hooks/background-class"
import {useLocalization} from "../../hooks/localization"
import yesIcon from '../../assets/images/icons/alarm/ok.png'
import cancelIcon from "../../assets/images/icons/alarm/cancel.png"
import Webcam from "react-webcam"

const doors = [yellowDoorIcon, blueDoorIcon, pingDoorIcon, limeDoorIcon]
const colors = ["yellow", "blue", "pink", "lime"]

type PopupType = {
    onClickYes: () => void
    onClickNo: () => void
}
const NumberBlockPopup = memo(function NumberBlockPopup(props: PopupType) {
    const {onClickYes, onClickNo} = props
    const {t} = useLocalization()
    return (
        <div className="number-block-popup-overlay">
            <div className="number-block-popup-container">
                <div className="number-block-message">
                    <span>{t("are_you_sure_you_want_to_block_this_caller")}</span>
                </div>
                <div className="number-block-actions">
                    <span onClick={onClickYes}>
                        <img src={yesIcon} alt="" />
                        {t("yes")}
                    </span>
                    <span onClick={onClickNo}>
                        <img src={cancelIcon} alt="" />
                        {t("no")}
                    </span>
                </div>
            </div>
        </div>
    )
})


export const CallScreen = memo(function CallScreen() {
    const [, , backgroundStyle] = useBackground()
    const {t} = useLocalization()
    const videoElement = useRef(null)
    const dispatch = useDispatch()
    const gateway = useCurrentGateway()
    const orientation = useOrientation()
    const [rpc] = useRPC()
    const intercomState = useSelector(state => state.control.intercomState)
    const bellStatus = useSelector(state => state.control.bellStatus)
    const doorList = useSelector(state => state.control.doorList)
    const doorBrandList = useSelector(state => state.control.doorBrandList)
    const intercomSoundLevelDB = useSelector(state => state.control.sound.intercom_level)
    const incomingCallSoundLevelDB = useSelector(state => state.control.sound.incoming_call_outside_panel_level)
    const hasUSBCamera = gateway?.settings.intercom.has_usb_camera
    const cameraList = useSelector(state => state.control.cameraList)
    const cameraBrandList = useSelector(state => state.control.cameraBrandList)
    const cameraBrandControl = cameraBrandList.find(r => r.name === "T-Bell")


    const {status, info, is_incoming_call, name: intercomName, call_time: callTime} = intercomState
    const [cameraId, setCameraId] = useState<string>(null)
    const [noImage, setNoImage] = useState(false)
    const [name, setName] = useState("")
    const [mediaPath, setMediaPath] = useState<MediaStream>(null)
    const [isCallOpen, setIsCallOpen] = useState(false)
    const [isCallStarted, setIsCallStarted] = useState(false)
    const [isIndoorCall, setIsIndoorCall] = useState(false)
    const [intercomSoundLevel, setIntercomSoundLevel] = useState(0)
    const [isDoorBell, setIsDoorBell] = useState(false);
    const [disabledLookup, setDisabledLookup] = useState({})
    const [callTimeState, setCallTimeState] = useState(0)
    const [showPopup, setShowPopup] = useState(false)

    const [imgSrc, setImgSrc] = useState(null)
    const videoConstraints = {
        width: 1024,
        height: 600,
        facingMode: "user"
    }
    const closeCam = useCallback(() => {
        try {
            let stream = videoElement.current.stream
            const tracks = stream.getTracks()
            tracks.forEach(track => track.stop())
            window.stop()
            console.log("Camera closed!")
        } catch (err) {}
    }, [videoElement])

    const activeCamera = useCallback(() => {
        const tOut = setTimeout(() => {
            try {
                const imageSrc = videoElement.current.getScreenshot()
                setImgSrc(imageSrc)
                rpc.UploadTBellImage(imageSrc)
            } catch (e) {}
        }, 1500)
        return () => clearTimeout(tOut)
    }, [rpc, videoElement, setImgSrc])

    useEffect(() => {
        activeCamera()
    }, [activeCamera])



    const answerCall = useCallback(
        (responseMessage: string, info?: number) => {
            rpc.answerIntercomRequest({response: responseMessage, info: info})
            setCallTimeState(info)
        },
        [rpc]
    )

    const openDoor = useCallback(
        (door_id: string, info?: number) => {
            if (disabledLookup[door_id]) {
                return
            }

            rpc.openDoor(door_id)

            setDisabledLookup(lookup => ({
                ...lookup,
                [door_id]: true
            }))

            setTimeout(() => {
                setDisabledLookup(lookup => ({
                    ...lookup,
                    [door_id]: false
                }))
            }, 3000)
        },
        [disabledLookup, rpc]
    )

    const hangUp = useCallback(() => {
        if (isIndoorCall) {
            dispatch(setBellStatusAction(null))
            closeCam()
            window.close()
        } else {
            if (status === "special" || status === "incoming") { //todo
                dispatch(setIntercomStatusAction("ended", true))
                answerCall("hang-up", callTimeState)
                window.stop()
                window.close()
            } else {
                answerCall("hang-up", callTimeState)
            }
        }
        setCallTimeState(callTime)
    }, [answerCall, dispatch, isIndoorCall, status, closeCam, callTimeState, callTime])

    const setInfo = useCallback(
        (info: IntercomModel) => {
            let name = intercomName
            let cameraId: string = null

            if (info) {
                if (info.camera_id) {
                    cameraId = info.camera_id
                }

                if (!name) {
                    if (info.name) {
                        name = info.name
                    } else if (info.sip_id) {
                        name = info.sip_id + " " + t("NUMBER_NO")
                    }
                }
            }

            if (cameraId) {
                //id gelmiyor ve else düşüyor
                setCameraId(cameraId)
                setNoImage(false)
            } else {
                setNoImage(true)
            }

            setName(name ?? t("UNKNOWN_NUMBER"))
        },
        [t, intercomName]
    )

    const changeSound = useCallback(
        (newLevel: number) => {
            if (newLevel < 0) {
                changeSound(0)
            } else if (newLevel > 100) {
                changeSound(100)
            } else {
                rpc.updateSoundLevel(newLevel).then(() => {
                    setIntercomSoundLevel(newLevel)
                })
            }
        },
        [rpc]
    )

    const increaseSound = useCallback(() => {
        changeSound(intercomSoundLevel + 10)
    }, [changeSound, intercomSoundLevel])

    const decreaseSound = useCallback(() => {
        changeSound(intercomSoundLevel - 10)
    }, [changeSound, intercomSoundLevel])

    useEffect(() => {
        if (isCallStarted) {
            setIntercomSoundLevel(intercomSoundLevelDB)
        } else {
            setIntercomSoundLevel(incomingCallSoundLevelDB)
        }
    }, [isCallStarted, intercomSoundLevelDB, incomingCallSoundLevelDB])

    useEffect(() => {
        if (isCallOpen && videoElement.current) {
            videoElement.current.srcObject = mediaPath
        }

        return () => {
            if (mediaPath) {
                mediaPath.getTracks()[0].stop()
            }
        }
        // eslint-disable-next-line
    }, [mediaPath, isCallOpen])


    useEffect(() => {
        if (bellStatus === "incoming") {
            rpc.getCameraList().then(camera => dispatch(setEntityListAction(camera, "camera")))
        }
    }, [bellStatus, rpc, dispatch])

    useEffect(() => {
        switch (status) {
            case "special":
                setInfo(info)
                setIsCallOpen(true)
                setIsCallStarted(true)
                break
            case "incoming":
                setInfo(info)
                setIsCallOpen(true)
                setIsCallStarted(false)
                setIsDoorBell(false)
                setCallTimeState(callTime)
                break
            case "timeout":
            case "rejected":
            case "ended":
            case "failed":
            case "cancelled":
                if (!isIndoorCall) {
                    setIsCallOpen(false)
                    setIsCallStarted(false)
                    setIsDoorBell(false)
                    intercomState.info = null
                    setInfo(intercomState.info)
                    setShowPopup(false)
                }
                break
            case "in_call":
                setIsCallStarted(true)
                break
        }
    }, [status, isCallStarted, isCallOpen, isIndoorCall, cameraId, info, setInfo, intercomState, callTime])


    useEffect(
        () => {
            if (bellStatus === "incoming") {
                if (isCallOpen) {
                    return
                }

                if (hasUSBCamera) {
                    setName(t("DOORBELL"))
                    navigator.mediaDevices
                        .getUserMedia({video: true})
                        .then(stream => {
                            if (mediaPath !== stream) {
                                setMediaPath(stream)
                                setNoImage(false)
                                setIsDoorBell(false)
                            }
                        })
                        .finally(() => {
                            setIsCallOpen(true)
                            setIsCallStarted(true)
                            setIsIndoorCall(true)
                        })
                } else {
                    setName(t("DOORBELL"))
                    setNoImage(true)
                    setIsCallOpen(true)
                    setIsCallStarted(true)
                    setIsIndoorCall(true)
                    setIsDoorBell(true)

                    //camera
                    console.log(cameraList)
                    //is_door_bell true ise o kameranın id'si ile görüntü açılacak
                }
            } else {
                if (isIndoorCall) {
                    setIsIndoorCall(false)
                    setIsCallStarted(false)
                    setIsCallOpen(false)
                    setIsDoorBell(false)
                }
            }
        },
        // eslint-disable-next-line
        [bellStatus, isCallOpen, isIndoorCall, mediaPath]
    )

    const onClickBlockNumber = useCallback(() => {
        setShowPopup(false)
        answerCall("block-number", callTime)
    }, [answerCall, callTime])


    useEffect(() => {
        let timeout
        if (isCallOpen) {
            timeout = setTimeout(() => {
                hangUp()
            }, 3 * 60 * 1000)
        }

        return () => {
            clearTimeout(timeout)
        }

        // eslint-disable-next-line
    }, [isCallOpen])

    //todo fix
    useEffect(() => {
        if (isCallOpen) {
            WindowUtil.cancel()
        } else {
            WindowUtil.stop()
        }
    }, [isCallOpen])


    if (!isCallOpen) {
        return null
    }

    return (
        <>
        {showPopup && <NumberBlockPopup onClickNo={() => setShowPopup(false)} onClickYes={onClickBlockNumber}/>}
        <div className={"call-screen-wrapper " + orientation} style={backgroundStyle}>
            {cameraList.map(res =>
                renderIf(isIndoorCall && res.is_door_bell && isDoorBell)(() => (
                    <CameraRenderer className="preview" mode="rtsp" id={res.id} quality={720} />
                ))
            )}
            {cameraList.map(res =>
                renderIf(
                    res.is_door_bell &&
                        cameraBrandControl?.id === res?.camera_brand_id &&
                        cameraBrandControl.mjpeg.is_touch_bell
                )(() => <CameraRenderer className="preview" mode="auto" id={res.id} quality={720} />)
            )}

            {renderIf(!isIndoorCall && !noImage)(
                () => (
                    <CameraRenderer className="preview" mode="auto" id={cameraId} quality={720} />
                ),
                () =>
                    renderIf(!noImage)(() => (
                        <Webcam
                            audio={false}
                            ref={videoElement}
                            //onPlay={activeCamera}
                            onUserMedia={activeCamera}
                            videoConstraints={videoConstraints}
                            forceScreenshotSourceSize={true}
                            screenshotQuality={1}
                            screenshotFormat="image/jpeg"
                        />
                        //<video ref={videoElement} autoPlay={true}/>
                    ))
            )}

            {renderIf(name)(() => (
                <div className="call-header">
                    {renderIf(!isCallStarted && !isIndoorCall)(() => (
                        <img src={iconCallGreen} alt="" />
                    ))}
                    <span className="name">{name}</span>
                    {renderIf(!isCallStarted && !isIndoorCall)(
                        () => (
                            <span>{is_incoming_call ? t("CALLING") : t("OUT_CALLING")}</span>
                        ),
                        () => (
                            <Timer />
                        )
                    )}
                </div>
            ))}

            <div className="call-control-right">
                {renderIf(!isIndoorCall && info != null && info?.door_id_list.length > 0)(() =>
                    info.door_id_list.map((door_id, index) => {
                        const door = doorList.find(door => door.id === door_id)
                        const doorBrand = doorBrandList.find(
                            doors => doors.id === door.door_brand_id && doors.name === "DTMF"
                        )
                        if (!door) {
                            return null
                        }
                        const disablationColorControl = doorBrand
                            ? intercomState.status !== "in_call"
                                ? "grayscale(100%)"
                                : "none"
                            : "none"
                        const disablationControl = doorBrand
                            ? intercomState.status === "in_call"
                                ? false
                                : true
                            : false
                        return (
                            <button
                                style={{filter: `${disablationColorControl}`}}
                                key={`door_${door.id}`}
                                disabled={disablationControl}
                                className={
                                    "button button-transparent lg " +
                                    colors[index % colors.length] +
                                    (disabledLookup[door.id] ? " disabled" : "")
                                }
                                {...onClickHandler(() => openDoor(door.id, callTimeState))}
                            >
                                <img src={doors[index % doors.length]} alt="" />
                                <span>{door.name}</span>
                            </button>
                        )
                    })
                )}
            </div>
            <div className="call-control-bottom">
                {renderIf(!isCallStarted && !isIndoorCall && is_incoming_call)(() => (
                    <button
                        className="button button-transparent color-green lg"
                        {...onClickHandler(() => answerCall("answer", callTime))}
                    >
                        <img src={iconCallGreen} alt="" />
                        <span>{t("ANSWER_CALL")}</span>
                    </button>
                ))}
                <button className="button button-transparent color-red lg" {...onClickHandler(hangUp)}>
                    <img src={iconCallEndRed} alt="" />
                    <span>
                        {intercomState.status === "special"
                            ? t("END_WATCHING")
                            : intercomState.status === "incoming" && !isCallStarted && !isIndoorCall
                            ? t("REJECT_CALL")
                            : t("END_CALL")}
                    </span>
                </button>
                {renderIf(!isIndoorCall && intercomState.status !== "special" && is_incoming_call)(() => (
                    <button
                        className="button button-transparent color-red lg"
                        {...onClickHandler(() => setShowPopup(true))}
                    >
                        <img src={iconBlockRed} alt="" />
                        <span>{t("block_the_number")}</span>
                    </button>
                ))}
            </div>
            {renderIf(!isIndoorCall && intercomState.status !== "special")(() => (
                <div className="call-control-left">
                    <div className="button-container">
                        <span className="button" {...onClickHandler(increaseSound)}>
                            +
                        </span>
                        <span className="value">{intercomSoundLevel}</span>
                        <span className="button" {...onClickHandler(decreaseSound)}>
                            -
                        </span>
                    </div>
                </div>
            ))}
        </div>
        </>
    )
})
